import type {
	DialogContent,
	DialogSlot,
} from 'store/ui';
import {
	type MaybeRef,
	unref,
} from 'vue';

const DIALOGS_CONTAINER_ID = 'dialogs';

/**
 * Normalizes dialog content into a renderable slot list.
 *
 * @param content - Dialog content that may be a plain value, ref, or computed ref.
 * @returns The normalized dialog slots.
 */
function normalizeDialogSlots(content: DialogContent | DialogSlot['slot'] | undefined): DialogSlot[] {
	const resolved = resolveDialogValue(content);

	if (
		!resolved
		|| typeof resolved === 'string'
	) {
		return [];
	}

	const slots = Array.isArray(resolved)
		? resolved
		: [resolved];

	return slots.map((slot) => unref(slot));
}

/**
 * Resolves text slot content from a dialog slot definition.
 *
 * @param slot - Dialog slot content that may be a plain value, ref, or computed ref.
 * @returns Text content when the slot resolves to a string.
 */
function resolveDialogTextSlot(slot: DialogSlot['slot']): string | undefined {
	const resolved = resolveDialogValue(slot);

	return typeof resolved === 'string'
		? resolved
		: undefined;
}

/**
 * Resolves a plain value, ref, or computed ref without invoking function props.
 *
 * @param value - The value to resolve.
 * @returns The unwrapped value.
 */
function resolveDialogValue<T>(value: MaybeRef<T> | undefined): T | undefined {
	if (typeof value === 'undefined') {
		return undefined;
	}

	return unref(value);
}

export {
	DIALOGS_CONTAINER_ID,
	normalizeDialogSlots,
	resolveDialogTextSlot,
	resolveDialogValue,
};
